/**
 * Single source of truth for which files an agent can be taught from.
 *
 * This list mirrors TRAINING_DATA_EXTENSION_CONTENT_TYPES in the content-management
 * presigned-URL lambda. That lambda is the authority — it rejects anything else with a
 * 400 — but a user should never have to round-trip to the server to learn a file is not
 * accepted, so Create Agent and Edit Agent both filter against this list first.
 *
 * The canonical list is contracts/knowledge-file-types.json, vendored into both backend
 * repos with a drift test in each. When adding a format, update it there too or the
 * upload will 400 despite the picker offering it.
 *
 * Validation is by EXTENSION, never by File.type. Browsers report these inconsistently:
 * Windows gives .csv the MIME application/vnd.ms-excel whenever Excel is installed, and
 * .md commonly arrives as an empty string, so a MIME check silently rejects valid files.
 *
 * Legacy .doc and .xls are deliberately absent: the embedding pipeline reads text with
 * python-docx and openpyxl, neither of which handles the old binary formats, so they
 * would upload successfully and then be unreadable by the agent.
 */

export const KNOWLEDGE_FILE_EXTENSIONS = [
  ".pdf",
  ".md",
  ".txt",
  ".csv",
  ".json",
  ".docx",
  ".xlsx",
  ".pptx",
] as const;

/** Value for an <input type="file"> accept attribute. */
export const KNOWLEDGE_FILE_ACCEPT = KNOWLEDGE_FILE_EXTENSIONS.join(",");

/** Human-readable list for helper text, e.g. "PDF, MD, TXT, CSV, JSON, DOCX, XLSX". */
export const KNOWLEDGE_FILE_LABEL = KNOWLEDGE_FILE_EXTENSIONS.map((ext) =>
  ext.slice(1).toUpperCase()
).join(", ");

export const isSupportedKnowledgeFile = (file: File): boolean => {
  const name = file.name.toLowerCase();
  return KNOWLEDGE_FILE_EXTENSIONS.some((ext) => name.endsWith(ext));
};

/**
 * Split a selection into files we can upload and files we cannot.
 *
 * Returns the rejected names too: `accept` only filters the file picker's default view,
 * so a user who switches it to "All files" can still choose an unsupported file.
 * Dropping those silently reads as the upload being broken.
 */
export const partitionKnowledgeFiles = (
  files: File[]
): { accepted: File[]; rejectedNames: string[] } => {
  const accepted: File[] = [];
  const rejectedNames: string[] = [];

  files.forEach((file) => {
    if (isSupportedKnowledgeFile(file)) {
      accepted.push(file);
    } else {
      rejectedNames.push(file.name);
    }
  });

  return { accepted, rejectedNames };
};

/** Message for a rejected selection, or null when everything was accepted. */
export const knowledgeFileRejectionMessage = (
  rejectedNames: string[]
): string | null => {
  if (rejectedNames.length === 0) return null;
  return `Skipped ${rejectedNames.join(", ")} — supported formats are ${KNOWLEDGE_FILE_LABEL}.`;
};

/**
 * Strips the uniqueness suffix `sanitizeFileName` appends before upload, so a file the
 * user picks can be compared against one already attached.
 *
 * Stored names look like `brand-deck-1785667968297-88mzhe.pptx`: the base name, a
 * 13-digit `Date.now()`, and a short random id. Comparing raw names would therefore
 * never match — every upload of the same document produces a different stored name,
 * which is exactly how duplicates accumulated unnoticed.
 *
 * The timestamp is matched as exactly 13 digits rather than `\d+` so a genuine filename
 * like `invoice-2024-q3.pdf` is not mistaken for a suffix and truncated.
 */
const UPLOAD_SUFFIX_RE = /-\d{13}-[a-z0-9]{1,6}(?=\.[^.]+$|$)/;

export const knowledgeFileIdentity = (fileName: string): string => {
  if (!fileName) return "";
  const withoutSuffix = fileName.replace(UPLOAD_SUFFIX_RE, "");
  const lastDot = withoutSuffix.lastIndexOf(".");
  const base = lastDot !== -1 ? withoutSuffix.slice(0, lastDot) : withoutSuffix;
  const extension = lastDot !== -1 ? withoutSuffix.slice(lastDot) : "";

  // Mirror sanitizeFileName's normalisation so "Brand Deck.pptx" and the stored
  // "brand-deck-...pptx" resolve to the same identity.
  const normalized = base
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/gi, "")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

  return `${normalized}${extension.toLowerCase()}`;
};

/**
 * Split a selection into files that are genuinely new and ones already present.
 *
 * `existingNames` should include both files already attached to the agent and any
 * staged in the current batch — re-picking the same file twice before saving is just
 * as much a duplicate as re-uploading one that is already attached.
 */
export const partitionDuplicateKnowledgeFiles = (
  files: File[],
  existingNames: string[]
): { fresh: File[]; duplicateNames: string[] } => {
  const seen = new Set(existingNames.map(knowledgeFileIdentity));
  const fresh: File[] = [];
  const duplicateNames: string[] = [];

  files.forEach((file) => {
    const identity = knowledgeFileIdentity(file.name);
    if (seen.has(identity)) {
      duplicateNames.push(file.name);
    } else {
      // Add as we go so two copies within the same selection are caught too.
      seen.add(identity);
      fresh.push(file);
    }
  });

  return { fresh, duplicateNames };
};

/** Toast copy for a duplicate selection, or null when there were none. */
export const knowledgeFileDuplicateMessage = (
  duplicateNames: string[]
): string | null => {
  if (duplicateNames.length === 0) return null;
  if (duplicateNames.length === 1) {
    return `"${duplicateNames[0]}" is already attached to this agent — skipped.`;
  }
  return `${duplicateNames.length} files are already attached and were skipped: ${duplicateNames.join(", ")}.`;
};
